/**
 * Catalog-driven auth UI profiles for provider onboarding Connect step.
 * Uses published stepKey from Service Onboarding, not hardcoded capability IDs.
 */

export type AuthProfileKind = 'token' | 'authorize' | 'accounts' | 'default';

/** Mirror of admin-api ADAPTER_CONFIG_STEP_TO_CAPABILITY (step → capability). */
export const ADAPTER_CONFIG_STEP_TO_CAPABILITY: Record<string, Record<string, string>> = {
  'income-verification': {
    authorize_token: 'INCOME.GET_TX_AUTHORIZE_TOKEN',
    authorize_login: 'INCOME.GET_TX_AUTHORIZE_LOGIN',
    authorize_mfa: 'INCOME.GET_TX_AUTHORIZE_MFA',
    accounts_detail: 'INCOME.GET_TX_ACCOUNTS_DETAIL',
  },
  get_transaction: {
    authorize_token: 'INCOME.GET_TX_AUTHORIZE_TOKEN',
    authorize_login: 'INCOME.GET_TX_AUTHORIZE_LOGIN',
    authorize_mfa: 'INCOME.GET_TX_AUTHORIZE_MFA',
    accounts_detail: 'INCOME.GET_TX_ACCOUNTS_DETAIL',
  },
  'identity-verification': {
    token: 'IDENTITY.TPA_TOKEN',
    dossier: 'IDENTITY.TPA_DOSSIER',
  },
  idv_ver: {
    token: 'IDENTITY.TPA_TOKEN',
    dossier: 'IDENTITY.TPA_DOSSIER',
  },
  'ai-gateway': {
    generate: 'AI.GENERATE',
  },
};

export function capabilityToStepKey(adapterServiceKey: string, capability: string): string | null {
  const key = adapterServiceKey.trim().toLowerCase();
  const steps = ADAPTER_CONFIG_STEP_TO_CAPABILITY[key];
  if (!steps) return null;
  if (key === 'ai-gateway') return 'ai_generate';
  for (const [stepKey, cap] of Object.entries(steps)) {
    if (cap === capability) return stepKey;
  }
  return null;
}

export function authProfileKindFromStepKey(stepKey: string | null | undefined): AuthProfileKind {
  const normalized = stepKey?.trim().toLowerCase() ?? '';
  if (!normalized) return 'default';
  if (normalized === 'authorize_token' || normalized === 'token') return 'token';
  if (normalized === 'authorize_login' || normalized === 'authorize_mfa') return 'authorize';
  if (normalized === 'accounts_detail') return 'accounts';
  return 'default';
}

let stepKeyFallbackWarned = false;

export function resolveStepKey(input: {
  stepKey?: string | null;
  adapterServiceKey?: string | null;
  capability?: string | null;
}): string | null {
  const fromCatalog = input.stepKey?.trim();
  if (fromCatalog) return fromCatalog;
  const adapterKey = input.adapterServiceKey?.trim();
  const capability = input.capability?.trim();
  if (!adapterKey || !capability) return null;
  const fromFallback = capabilityToStepKey(adapterKey, capability);
  if (fromFallback && !stepKeyFallbackWarned && typeof console !== 'undefined') {
    stepKeyFallbackWarned = true;
    console.warn(
      '[authProfileKind] ADAPTER_CONFIG_STEP_TO_CAPABILITY fallback used; publish stepKey in catalog.',
      { adapterKey, capability },
    );
  }
  return fromFallback;
}

export function authProfileKindForCapability(input: {
  stepKey?: string | null;
  adapterServiceKey?: string | null;
  capability?: string | null;
}): AuthProfileKind {
  return authProfileKindFromStepKey(resolveStepKey(input));
}

/** Optional HTTP token preflight — available on any endpoint auth profile. */
export const TOKEN_PREFLIGHT_AUTH_CONFIG_FIELDS = [
  'tokenPreflightEnabled',
  'useTokenPreflight',
  'enableTokenPreflight',
  'tokenPath',
  'authorizeTokenPath',
  'tokenUrl',
  'authorizeTokenUrl',
  'tokenResponsePath',
  'tokenField',
  'tokenTargetHeaderName',
  'targetHeaderName',
  'authorizeHeaderName',
  'tokenOutputPayloadField',
  'tokenRequestMapping',
  'tokenResponseMapping',
  'authorizeTokenRequestMapping',
  'authorizeTokenResponseMapping',
  'tokenPreflightType',
] as const;

/** Keys persisted on endpointMappings[].authConfig per profile kind. */
export function authConfigFieldsForProfile(kind: AuthProfileKind): string[] {
  switch (kind) {
    case 'accounts':
      return [
        'accountsApiKeyHeaderName',
        'accountsHeaderName',
        'accountsApiKeyEnv',
        'flinksApiKeyEnv',
        'accountsApiKey',
        'flinksApiKey',
        'accountsApiKeyPrefix',
        ...TOKEN_PREFLIGHT_AUTH_CONFIG_FIELDS,
      ];
    case 'token':
    case 'authorize':
      return [
        'headerName',
        'apiKey',
        'key',
        'apiKeyEnv',
        'apiKeyPrefix',
        ...TOKEN_PREFLIGHT_AUTH_CONFIG_FIELDS,
      ];
    default:
      return [];
  }
}

export function pickAuthConfigForProfile(
  authConfig: Record<string, string>,
  kind: AuthProfileKind,
): Record<string, string> {
  const keys = authConfigFieldsForProfile(kind);
  if (keys.length === 0) return { ...authConfig };
  const out: Record<string, string> = {};
  for (const key of keys) {
    const value = authConfig[key];
    if (value !== undefined && value !== '') out[key] = value;
  }
  return out;
}

export function mergeEffectiveAuthConfig(
  vendorAuth: Record<string, string> | undefined,
  endpointAuth: Record<string, string> | undefined,
): Record<string, string> {
  return { ...(vendorAuth ?? {}), ...(endpointAuth ?? {}) };
}

/** Infer Connect-step auth type from merged endpoint + vendor authConfig fields. */
export function inferAuthTypeFromAuthConfig(
  authConfig: Record<string, string>,
  profileKind: AuthProfileKind = 'default',
): 'none' | 'api-key' | 'bearer' | 'basic' | 'oauth-client-credentials' {
  if (profileKind === 'accounts') return 'api-key';
  if (authConfig.clientId?.trim() || authConfig.clientSecret?.trim() || authConfig.clientSecretEnv?.trim()) {
    return 'oauth-client-credentials';
  }
  if (authConfig.username?.trim()) return 'basic';
  if (authConfig.token?.trim() || authConfig.tokenEnv?.trim()) return 'bearer';
  if (
    authConfig.headerName?.trim() ||
    authConfig.apiKeyEnv?.trim() ||
    authConfig.apiKey?.trim() ||
    authConfig.key?.trim() ||
    authConfig.accountsApiKey?.trim() ||
    authConfig.flinksApiKey?.trim() ||
    authConfig.accountsApiKeyEnv?.trim() ||
    authConfig.flinksApiKeyEnv?.trim()
  ) {
    return 'api-key';
  }
  return 'none';
}

export function defaultRuntimeMappingTemplateForStepKey(stepKey: string | null | undefined): string {
  const kind = authProfileKindFromStepKey(stepKey);
  if (kind === 'authorize') {
    const normalized = stepKey?.trim().toLowerCase() ?? '';
    if (normalized === 'authorize_mfa') {
      return `{
  "RequestId": p.requestId,
  "SecurityResponses": p.securityResponses
}`;
    }
    return `{
  "username": p.credentials.username,
  "password": p.credentials.password,
  "institution": p.credentials.institution
}`;
  }
  if (kind === 'accounts') {
    return `{
  "request_id": p.requestId
}`;
  }
  return '';
}
